// FILE: src/components/journey/onboarding/JourneyOnboarding.js
//
// Journey Activation onboarding surface. Ships DARK behind
// journeyOnboardingEnabled (default false — flag OFF renders nothing, so
// production behavior is unchanged).
//
// Rendered by ExamSelector's `journeySlot` (certification-select screen),
// between the hero and the cert grid — it never blocks the grid, which stays
// fully interactive below it.
//
// Treatments (journeyService.computeJourneyTreatment):
//   first_time  navy coach panel, four one-click stages + not-sure + dismiss
//   dormant     one-line strip; "Show me options" expands the same stages
//   collapsed   right-aligned "Set your journey" link only
//   null        nothing
//
// Every recommendation routes into an EXISTING product flow via props wired
// in App.js — micro-practice reuses the targeted-test pipeline (count 5),
// Preparing reuses the baseline flow, Testing reuses practice/full sims,
// Study Guide is the existing 'plan' view. No parallel session, baseline, or
// study-plan implementation exists here.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLearnerAuth } from '../../../contexts/LearnerAuthContext';
import { isFeatureEnabled } from '../../../config/featureFlags';
import { getAllExams } from '../../../config/exams';
import { loadLearnerSessions } from '../../../services/performanceHistoryService';
import {
  JOURNEY_STAGES,
  computeJourneyTreatment,
  getExploringContent,
  getJourneyState,
  hasLocalIntent,
  persistJourneyPatch,
  trackJourneyEvent,
  trackPromptViewedOnce,
} from '../../../services/journeyService';
import CoachAvatar from '../../coach/CoachAvatar';
import styles from './JourneyOnboarding.module.css';

const STAGE_ICONS = { exploring: '🧭', learning: '📖', preparing: '🎯', testing: '🚩' };

const COACH_LINES = {
  exploring: 'Smart place to start. Get the lay of the land before you commit to anything.',
  learning: "Best way to learn is by doing. Five real questions with a full explanation after each one.",
  preparing: "Let's find your gaps first, then aim your study time where it counts.",
  testing: "Let's see where you stand under exam conditions.",
  unsure: "No problem — look around at your own pace. If you want a taste, five quick questions is a low-stakes way in.",
};

export default function JourneyOnboarding({
  onStartMicro,      // (certId) -> 5-question micro-practice (targeted-test pipeline)
  onStartBaseline,   // (certId) -> existing baseline flow (App.handleStartCert)
  onStartMode,       // (certId, 'practice'|'full') -> existing session modes
  onNavigate,        // (view) -> App.setView ('plan' = Study Guide / Study Plan)
}) {
  const enabled = isFeatureEnabled('journeyOnboardingEnabled');
  const { user, profile, loading } = useLearnerAuth();

  // phase: 'hidden' | 'panel' | 'reco' | 'collapsed' | 'strip' | 'strip_open' | 'micro_done'
  const [phase, setPhase] = useState('hidden');
  const [journey, setJourney] = useState({});
  const [stage, setStage] = useState(null);          // active reco stage ('unsure' included)
  const [pendingCert, setPendingCert] = useState(null); // exploring/testing: cert chosen, options shown
  const treatmentRef = useRef(null);
  // Once the learner interacts (select/dismiss/expand), evaluation re-runs
  // must never clobber what they're looking at.
  const interactedRef = useRef(false);
  // One sessions fetch per mount — re-evaluations (e.g. when the profile row
  // arrives after auth) reuse the same promise instead of re-querying.
  const sessionsPromiseRef = useRef(null);
  const seenPersistedRef = useRef(false);
  // Latest profile each render, plus a queue of journey patches made while
  // the profile row was still loading. Persisting against a stale/null
  // profile would overwrite preferences.journey and destroy saved state
  // (stage, dismissal history), so writes wait for a loaded profile and
  // always merge onto the freshest row.
  const profileRef = useRef(null);
  const pendingPatchRef = useRef({});
  const journeyRef = useRef({});
  profileRef.current = profile;
  journeyRef.current = journey;

  const activeCerts = useMemo(
    () => getAllExams().filter((e) => !e.comingSoon).map((e) => ({ id: e.examId, label: e.displayName })),
    []
  );

  // Persist a journey patch and keep local state in sync (profile in context
  // stays stale until the next auth refresh — local overlay covers the gap).
  // Writes queue until the profile row object exists; the evaluation effect
  // flushes the queue when it arrives.
  function persist(patch) {
    setJourney((prev) => ({ ...prev, ...patch }));
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    flushPersist();
  }

  function flushPersist() {
    const patch = pendingPatchRef.current;
    if (!user || !user.id) return;
    if (profileRef.current == null) return; // wait for the row — see refs above
    if (!patch || Object.keys(patch).length === 0) return;
    pendingPatchRef.current = {};
    // Send the full accumulated local view, not just the delta: sequential
    // writes merge onto a stale context profile, so a delta-only write could
    // regress fields an earlier write already persisted this session.
    persistJourneyPatch(user.id, profileRef.current, { ...journeyRef.current, ...patch });
  }

  // Evaluation is deliberately NOT one-shot: auth hydrates in stages (user
  // first, profile row later), and each stage re-runs this effect. The
  // previous run is cancelled and the newest run wins — a completed
  // evaluation is never cancelled retroactively, and learner interactions
  // latch the surface so re-runs can't clobber it. (A one-shot guard here
  // caused a field bug: the profile row arriving mid-fetch cancelled the
  // only evaluation and the surface latched hidden.)
  useEffect(() => {
    if (!enabled || loading) return;
    let cancelled = false;

    async function evaluate() {
      if (interactedRef.current) return;
      if (!user || !user.email_confirmed_at) return;
      const j = getJourneyState(profile);
      setJourney((prev) => ({ ...j, ...prev }));

      // Cheap suppression checks first (no network): local intent / baseline.
      const cheaplySuppressed = computeJourneyTreatment({ user, profile, sessions: [] }) === null;

      // Sessions are needed both for suppression and the micro-done handoff.
      const wantsMicroHandoff = j.stage === 'learning' && j.microStartedAt && !j.microDone;
      let sessions = [];
      if (!cheaplySuppressed || wantsMicroHandoff) {
        try {
          if (!sessionsPromiseRef.current) sessionsPromiseRef.current = loadLearnerSessions();
          sessions = await sessionsPromiseRef.current;
        } catch (_) { sessions = []; }
      }
      if (cancelled || interactedRef.current) return;

      // One-time handoff card after the 5-question micro-practice completes.
      if (wantsMicroHandoff
          && sessions.some((s) => s.startedAt && Date.parse(s.startedAt) >= Date.parse(j.microStartedAt))) {
        setStage('learning');
        setPhase('micro_done');
        return;
      }

      const treatment = computeJourneyTreatment({ user, profile, sessions });
      treatmentRef.current = treatment === 'dormant' ? 'dormant' : 'first_time';
      if (treatment === null) {
        // Reconcile device-local intent into the profile for cross-device
        // suppression — through the deferred queue, NEVER a direct write: a
        // direct write against a not-yet-loaded profile merges onto {} and
        // wipes saved journey state (field bug: erased stage/microStartedAt
        // and lost the micro-done handoff).
        if (hasLocalIntent() && !j.intentObservedAt && user.id) {
          persist({ intentObservedAt: new Date().toISOString() });
        }
        return;
      }

      if (!seenPersistedRef.current) {
        seenPersistedRef.current = true;
        persist({ lastSeenAt: new Date().toISOString() });
      }
      if (treatment === 'first_time') { setPhase('panel'); trackPromptViewedOnce('first_time'); }
      else if (treatment === 'dormant') { setPhase('strip'); trackPromptViewedOnce('dormant'); }
      else setPhase('collapsed');
    }

    flushPersist(); // profile may have just arrived — write queued patches
    evaluate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, loading, user, profile]);

  // Any learner interaction latches the surface against re-evaluation.
  function latch() { interactedRef.current = true; }

  if (!enabled || phase === 'hidden') return null;
  const treatment = treatmentRef.current || 'first_time';

  // ── Actions ────────────────────────────────────────────────────────────

  function selectStage(id) {
    latch();
    const prev = journey.stage;
    if (prev && prev !== id) {
      trackJourneyEvent('journey_stage_changed', { treatment, from: prev, to: id });
    }
    trackJourneyEvent('journey_stage_selected', { treatment, stage: id });
    persist({ stage: id, stageSelectedAt: new Date().toISOString() });
    setStage(id);
    setPendingCert(null);
    setPhase('reco');
    trackJourneyEvent('journey_recommendation_viewed', { treatment, stage: id });
  }

  function dismissPanel() {
    latch();
    trackJourneyEvent('journey_prompt_dismissed', { treatment: 'first_time' });
    persist({ promptDismissedAt: new Date().toISOString() });
    setPhase('collapsed');
  }

  function dismissStrip() {
    latch();
    trackJourneyEvent('journey_prompt_dismissed', { treatment: 'dormant' });
    const prior = Array.isArray(journey.stripDismissedAt) ? journey.stripDismissedAt : [];
    persist({ stripDismissedAt: [...prior, new Date().toISOString()] });
    setPhase('hidden');
  }

  function startReco(target, certId, extra = {}) {
    latch();
    trackJourneyEvent('journey_recommendation_started', {
      treatment, stage, cert: certId, target, contentId: extra.contentId,
    });
    if (target === 'micro') { persist({ cert: certId, microStartedAt: new Date().toISOString(), microDone: false }); onStartMicro(certId); }
    else if (target === 'baseline') { persist({ cert: certId }); onStartBaseline(certId); }
    else if (target === 'practice' || target === 'full') { persist({ cert: certId }); onStartMode(certId, target); }
    else if (target === 'plan') { persist({ microDone: true }); onNavigate('plan'); }
    // target 'article': anchor navigation handles it; cert remembered below.
    if (target === 'article') persist({ cert: certId });
  }

  // ── Fragments ──────────────────────────────────────────────────────────

  const certChips = (onPick, testPrefix) => (
    <div className={styles.certChips}>
      {activeCerts.map((c) => (
        <button
          key={c.id}
          type="button"
          className={styles.certChip}
          data-testid={`${testPrefix}-${c.id}`}
          onClick={() => onPick(c.id)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );

  const stageOptions = (compact) => (
    <div className={styles.options}>
      {JOURNEY_STAGES.map((s) => (
        <button
          key={s.id}
          type="button"
          className={styles.opt}
          data-testid={`journey-option-${s.id}`}
          onClick={() => selectStage(s.id)}
        >
          <span className={styles.optIcon} aria-hidden="true">{STAGE_ICONS[s.id]}</span>
          <span>
            <span className={styles.optLabel}>{s.label}</span>
            {!compact && <span className={styles.optBlurb}>{s.blurb}</span>}
          </span>
        </button>
      ))}
    </div>
  );

  const coachSays = (line, muted) => (
    <div className={`${styles.coachSays} ${muted ? styles.coachSaysMuted : ''}`}>
      <p className={styles.coachLabel}>⭐ Coach says</p>
      <p className={styles.coachLine}>{line}</p>
    </div>
  );

  // ── Recommendation body per stage ──────────────────────────────────────

  function recoBody() {
    if (stage === 'exploring') {
      if (!pendingCert) {
        return (
          <>
            <p className={styles.ctaLabel}>Which certification are you curious about?</p>
            {certChips((c) => setPendingCert(c), 'journey-cert-chip')}
          </>
        );
      }
      const content = getExploringContent(pendingCert);
      const certLabel = (activeCerts.find((c) => c.id === pendingCert) || {}).label || pendingCert;
      if (content) {
        return (
          <>
            <div className={styles.ctaRow}>
              <a
                className={styles.primaryBtn}
                style={{ textDecoration: 'none', display: 'inline-block' }}
                href={content.primary.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="journey-cta-article"
                onClick={() => startReco('article', pendingCert, { contentId: content.primary.contentId })}
              >
                Read: {content.primary.label} →
              </a>
              <a
                className={styles.secondaryBtn}
                style={{ textDecoration: 'none', display: 'inline-block' }}
                href={content.secondary.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="journey-cta-article-secondary"
                onClick={() => startReco('article', pendingCert, { contentId: content.secondary.contentId })}
              >
                {content.secondary.label}
              </a>
            </div>
            <p className={styles.sub}>Free guides — no signup walls. Come back whenever you're ready.</p>
          </>
        );
      }
      return (
        <>
          {coachSays(`${certLabel} guides are in the works. Meanwhile, the quickest way to explore is a few real questions.`, true)}
          <div className={styles.ctaRow}>
            <button type="button" className={styles.primaryBtn} data-testid="journey-cta-micro" onClick={() => startReco('micro', pendingCert)}>
              Try 5 quick questions →
            </button>
            <button type="button" className={styles.linkBtn} onClick={() => setPendingCert(null)}>Pick a different certification</button>
          </div>
          <p className={styles.sub}>About 8 minutes · explanations after each · no commitment</p>
        </>
      );
    }

    if (stage === 'learning') {
      return (
        <>
          <p className={styles.ctaLabel}>Start 5 quick questions:</p>
          {certChips((c) => startReco('micro', c), 'journey-cta-micro')}
          <p className={styles.sub}>About 8 minutes · full explanation after each question · starts building your study signals</p>
        </>
      );
    }

    if (stage === 'preparing') {
      return (
        <>
          <p className={styles.ctaLabel}>Set a baseline:</p>
          {certChips((c) => startReco('baseline', c), 'journey-cta-baseline')}
          <p className={styles.sub}>A short practice session · unlocks readiness tracking and targeted study</p>
        </>
      );
    }

    if (stage === 'testing') {
      if (!pendingCert) {
        return (
          <>
            <p className={styles.ctaLabel}>Which certification?</p>
            {certChips((c) => setPendingCert(c), 'journey-cert-chip')}
          </>
        );
      }
      return (
        <>
          <div className={styles.ctaRow}>
            <button type="button" className={styles.primaryBtn} data-testid="journey-cta-practice" onClick={() => startReco('practice', pendingCert)}>
              Start a practice exam →
            </button>
            <button type="button" className={styles.secondaryBtn} data-testid="journey-cta-full" onClick={() => startReco('full', pendingCert)}>
              Full simulation
            </button>
          </div>
          <p className={styles.sub}>Timed, exam-style · your readiness view opens when you finish</p>
        </>
      );
    }

    // 'unsure'
    return (
      <>
        <div className={styles.ctaRow}>
          <button type="button" className={styles.secondaryBtn} data-testid="journey-unsure-micro" onClick={() => { setStage('learning'); setPhase('reco'); trackJourneyEvent('journey_recommendation_viewed', { treatment, stage: 'learning' }); }}>
            Try 5 quick questions
          </button>
          <button type="button" className={styles.linkBtn} data-testid="journey-unsure-dismiss" onClick={dismissPanel}>
            I'll explore on my own
          </button>
        </div>
      </>
    );
  }

  // ── Render per phase ───────────────────────────────────────────────────

  if (phase === 'collapsed') {
    const stageDef = JOURNEY_STAGES.find((s) => s.id === journey.stage);
    const label = stageDef ? `Journey: ${stageDef.label} · change` : 'Set your journey';
    return (
      <div className={styles.wrap}>
        <div className={styles.collapsedRow}>
          <button type="button" className={styles.linkBtn} data-testid="journey-collapsed-link" onClick={() => { latch(); setPhase('panel'); trackPromptViewedOnce(treatment); }}>
            {label}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'strip' || phase === 'strip_open') {
    return (
      <div className={styles.wrap}>
        <div className={styles.strip} role="status" data-testid="journey-strip">
          <span className={styles.stripIcon} role="img" aria-label="Journey coach">🧭</span>
          <span className={styles.stripText}>
            {phase === 'strip'
              ? 'Welcome back. Want a hand picking a next step? Takes one click.'
              : "Where are you right now? I'll queue up one good next step."}
          </span>
          {phase === 'strip' && (
            <button type="button" className={styles.stripBtn} data-testid="journey-strip-expand" onClick={() => { latch(); setPhase('strip_open'); }}>
              Show me options
            </button>
          )}
          <button type="button" className={styles.stripDismiss} aria-label="Dismiss journey suggestion" data-testid="journey-strip-dismiss" onClick={dismissStrip}>
            ×
          </button>
          {phase === 'strip_open' && (
            <div className={styles.stripChips}>
              {JOURNEY_STAGES.map((s) => (
                <button key={s.id} type="button" className={styles.certChip} data-testid={`journey-option-${s.id}`} onClick={() => selectStage(s.id)}>
                  {STAGE_ICONS[s.id]} {s.label}
                </button>
              ))}
              <button type="button" className={styles.linkBtn} data-testid="journey-not-sure" onClick={() => { setStage('unsure'); persist({ stage: 'unsure' }); trackJourneyEvent('journey_stage_selected', { treatment, stage: 'unsure' }); setPhase('reco'); }}>
                not sure yet
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'micro_done') {
    return (
      <div className={styles.wrap}>
        <div className={styles.reco} data-testid="journey-micro-done">
          <span className={styles.stageChip}>📖 Learning</span>
          {coachSays('Nice work — 5 of 5 done. Your Study Guide has started tracking what you\'ve seen.')}
          <div className={styles.ctaRow}>
            <button type="button" className={styles.primaryBtn} data-testid="journey-cta-plan" onClick={() => startReco('plan', journey.cert)}>
              Open my Study Guide →
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={() => startReco('micro', journey.cert)}>
              5 more questions
            </button>
            <button type="button" className={styles.linkBtn} onClick={() => { persist({ microDone: true }); setPhase('hidden'); }}>
              Not now
            </button>
          </div>
          <p className={styles.sub}>Signals only — five questions show what you've covered, not a verdict.</p>
        </div>
      </div>
    );
  }

  if (phase === 'reco') {
    const stageDef = JOURNEY_STAGES.find((s) => s.id === stage);
    const chipLabel = stageDef ? `${STAGE_ICONS[stage]} ${stageDef.label}` : '🤔 Not sure yet';
    return (
      <div className={styles.wrap}>
        <div className={styles.reco} data-testid="journey-reco">
          <span className={`${styles.stageChip} ${stage === 'unsure' ? styles.stageChipMuted : ''}`}>{chipLabel}</span>
          {stage !== 'unsure' && (
            <button type="button" className={styles.changeBtn} data-testid="journey-change" onClick={() => { setPendingCert(null); setPhase('panel'); }}>
              change
            </button>
          )}
          {coachSays(COACH_LINES[stage] || COACH_LINES.unsure, stage === 'unsure')}
          {recoBody()}
        </div>
      </div>
    );
  }

  // phase === 'panel' — first-time treatment
  return (
    <div className={styles.wrap}>
      <div className={styles.panel} data-testid="journey-onboarding-panel">
        <button type="button" className={styles.closeBtn} aria-label="Dismiss journey panel" data-testid="journey-dismiss" onClick={dismissPanel}>
          ×
        </button>
        <div className={styles.headRow}>
          <div className={styles.avatarCol}>
            <CoachAvatar pose="notepad" heightPx={132} decorative />
          </div>
          <div>
            <p className={styles.eyebrow}>Your journey</p>
            <h2 className={styles.title}>Where are you right now?</h2>
            <p className={styles.subtitle}>One click — I'll line up the right next step. You can change this anytime.</p>
          </div>
        </div>
        {stageOptions(false)}
        <div className={styles.footerRow}>
          <button type="button" className={styles.notSure} data-testid="journey-not-sure" onClick={() => { setStage('unsure'); persist({ stage: 'unsure' }); trackJourneyEvent('journey_stage_selected', { treatment, stage: 'unsure' }); setPhase('reco'); }}>
            Not sure yet — show me around
          </button>
          <span className={styles.editNote}>Editable later — this never locks anything</span>
        </div>
      </div>
    </div>
  );
}
